import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { Eyebrow, Button, cx } from "@/components/kit";

const cases = [
  {
    id: "case-1",
    industry: "Regionaal MKB · Bouw & Installatie",
    title: "Van onvindbaar naar #1 in de regio.",
    quote:
      "Binnen zes maanden gingen we van drie offerteaanvragen per maand naar meer dan we konden aannemen.",
    metric: "+412%",
    metricLabel: "Organisch verkeer",
    image: "/v2/dashboard-seo.jpg",
    tags: ["SEO", "Webdesign AI", "Content"],
    source: "Eigenaar installatiebedrijf · Noord-Brabant",
  },
  {
    id: "case-2",
    industry: "ZZP · Coaching",
    title: "Volgeboekte agenda binnen 90 dagen.",
    quote:
      "Voor het eerst in jaren voel ik weer rust — de leads komen vanzelf binnen.",
    metric: "8×",
    metricLabel: "ROAS op Meta ads",
    image: "/v2/dashboard-hero.jpg",
    tags: ["Social Ads", "AI Marketing"],
    source: "Loopbaancoach · regio Utrecht",
  },
  {
    id: "case-3",
    industry: "E-commerce · DTC brand",
    title: "Webshop met driedubbele conversie.",
    quote:
      "Dezelfde bezoekers, drie keer zoveel verkopen. Het voelde bijna oneerlijk.",
    metric: "+186%",
    metricLabel: "Conversieratio",
    image: "/v2/glossy-dark.jpg",
    tags: ["Webflow", "CRO"],
    source: "Founder DTC-merk · Amsterdam",
  },
];

const CaseCard = ({ c, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const flip = i % 2 === 1;

  return (
    <motion.article
      ref={ref}
      data-testid={`case-card-${c.id}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-12 gap-6 lg:gap-12 items-center"
    >
      {/* Image */}
      <div className={cx("col-span-12 lg:col-span-7", flip && "lg:order-2")}>
        <div className="relative aspect-[16/10] overflow-hidden rounded-[1.5rem] bg-obsidian-800 border border-white/10">
          <motion.img
            style={{ y: imgY }}
            src={c.image}
            alt={c.title}
            loading="lazy"
            className="absolute inset-0 w-full h-[116%] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-obsidian/85 via-obsidian/20 to-electric/20 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />

          <div className="absolute bottom-6 left-6 lg:bottom-9 lg:left-9">
            <div className="glass-dark rounded-2xl px-5 py-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-300 mb-1">
                {c.metricLabel}
              </div>
              <div className="font-display font-black text-5xl lg:text-7xl text-ivory leading-none">
                {c.metric}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={cx("col-span-12 lg:col-span-5", flip && "lg:order-1")}>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-electric-300 mb-4">
          Case №{String(i + 1).padStart(2, "0")} · {c.industry}
        </div>
        <h3 className="font-display font-bold text-3xl lg:text-4xl text-ivory leading-[1.05] text-balance mb-6">
          {c.title}
        </h3>
        <p className="text-lg text-white/70 leading-relaxed border-l-2 border-electric pl-5">
          &ldquo;{c.quote}&rdquo;
        </p>
        <div className="mt-3 mb-6 pl-5 text-sm text-white/45" data-testid={`case-source-${c.id}`}>
          — {c.source} <span className="text-white/30">(naam bekend bij UPPIT)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.tags.map((t) => (
            <span
              key={t}
              className="text-[10px] font-mono uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-white/15 text-white/60"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
};

const Cases = () => (
  <section
    id="cases"
    data-testid="cases-section"
    className="relative bg-obsidian overflow-hidden py-24 lg:py-36"
  >
    <div className="absolute inset-0 grid-dark opacity-40" />
    <div className="glow-orb w-[600px] h-[600px] bg-electric/15 top-0 left-1/4" />

    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-16 lg:mb-24">
        <div className="lg:col-span-9">
          <Eyebrow dark>Resultaten · Selectie</Eyebrow>
          <h2 className="mt-6 font-display font-black text-ivory tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
            Resultaten die je <span className="text-gradient-blue">niet kunt negeren.</span>
          </h2>
        </div>
      </div>

      <div className="space-y-20 lg:space-y-32">
        {cases.map((c, i) => (
          <CaseCard key={c.id} c={c} i={i} />
        ))}
      </div>

      <Reveal delay={0.15} className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img
            src="/team/tim-avatar-2.webp"
            alt="Tim Roosendaal"
            loading="lazy"
            data-testid="cases-tim-avatar"
            className="w-14 h-14 rounded-full object-cover border-2 border-electric/50 shrink-0"
          />
          <p className="text-xl text-white/70 max-w-lg">
            Meer cases delen we graag persoonlijk — sommige onder NDA.{" "}
            <span className="text-white font-medium">Tim vertelt je er alles over.</span>
          </p>
        </div>
        <Button to="/contact" variant="outlineLight" testid="cases-cta" className="group">
          Vraag portfolio aan
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Button>
      </Reveal>
    </div>
  </section>
);

export default Cases;
